import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useRef, useState } from 'react';

import {
  type BulkCoachAssignmentEntry,
  type BulkCoachAssignmentErrorResponse,
  type BulkCoachAssignmentReview,
  useApplyBulkCoachAssignmentsMutation,
  useReviewBulkCoachAssignmentsMutation,
} from '../api/featureFlagsApi';

type BulkCoachAssignmentsDialogProps = {
  flagKey: string;
  open: boolean;
  onClose: () => void;
};

const STATUS_LABELS: Record<BulkCoachAssignmentEntry['status'], string> = {
  ready: 'Ready to add',
  already_assigned: 'Already assigned — no change',
  duplicate: 'Duplicate email',
  invalid_email: 'Invalid email',
  unknown_person: 'Person not found',
  ineligible_coach: 'Ineligible coach',
};

function entryExplanation(entry: BulkCoachAssignmentEntry): string | undefined {
  if (entry.reason === 'disabled') return 'This coach is disabled.';
  if (entry.reason === 'not_operational_coach') return 'This person is not an active coach.';
  if (entry.duplicate_of_line) return `Duplicates line ${entry.duplicate_of_line}.`;
  return undefined;
}

function errorPayload(error: unknown): BulkCoachAssignmentErrorResponse | undefined {
  if (!error || typeof error !== 'object' || !('data' in error)) return undefined;
  const data = (error as { data?: unknown }).data;
  if (!data || typeof data !== 'object') return undefined;
  return data as BulkCoachAssignmentErrorResponse;
}

export function BulkCoachAssignmentsDialog({
  flagKey,
  open,
  onClose,
}: BulkCoachAssignmentsDialogProps) {
  const [emailsText, setEmailsText] = useState('');
  const [review, setReview] = useState<BulkCoachAssignmentReview | null>(null);
  const [reviewConfirmed, setReviewConfirmed] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [reviewAssignments, { isLoading: isReviewing }] = useReviewBulkCoachAssignmentsMutation();
  const [applyAssignments, { isLoading: isApplying }] = useApplyBulkCoachAssignmentsMutation();
  const operationInFlightRef = useRef<'review' | 'apply' | null>(null);

  const handleReview = async () => {
    if (operationInFlightRef.current !== null) return;
    operationInFlightRef.current = 'review';
    setErrorMessage(null);
    try {
      const nextReview = await reviewAssignments({ flagKey, emailsText }).unwrap();
      setReview(nextReview);
      setReviewConfirmed(true);
    } catch (error) {
      setReview(null);
      setReviewConfirmed(false);
      setErrorMessage(errorPayload(error)?.message ?? 'Could not review these emails.');
    } finally {
      operationInFlightRef.current = null;
    }
  };

  const handleApply = async () => {
    if (!review || operationInFlightRef.current !== null) return;
    operationInFlightRef.current = 'apply';
    setErrorMessage(null);
    try {
      await applyAssignments({
        flagKey,
        emailsText,
        reviewFingerprint: review.review_fingerprint,
      }).unwrap();
      onClose();
    } catch (error) {
      const payload = errorPayload(error);
      if (payload?.review) {
        setReview(payload.review);
        setReviewConfirmed(false);
      }
      setErrorMessage(payload?.message ?? 'Could not apply these assignments.');
    } finally {
      operationInFlightRef.current = null;
    }
  };

  const handleEmailsChange = (value: string) => {
    if (operationInFlightRef.current !== null) return;
    setEmailsText(value);
    setReview(null);
    setReviewConfirmed(false);
    setErrorMessage(null);
  };

  const handleClose = () => {
    if (operationInFlightRef.current === 'apply') return;
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Paste coach emails for {flagKey}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Enter one email per line. Review must pass before any assignments are created.
        </Typography>
        <TextField
          label="Coach emails"
          value={emailsText}
          onChange={(event) => handleEmailsChange(event.target.value)}
          multiline
          minRows={7}
          fullWidth
          disabled={isReviewing || isApplying}
        />

        {errorMessage && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {errorMessage}
          </Alert>
        )}

        {review && (
          <Box sx={{ mt: 2 }}>
            <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
              <Chip label={`${review.summary.ready} ready`} color="success" size="small" />
              <Chip
                label={`${review.summary.already_assigned} already assigned`}
                color="info"
                size="small"
              />
              <Chip
                label={`${review.summary.blocking} blocking`}
                color={review.summary.blocking > 0 ? 'error' : 'default'}
                size="small"
              />
            </Stack>
            <List
              dense
              sx={{ maxHeight: 320, overflow: 'auto', border: 1, borderColor: 'divider' }}
            >
              {review.entries.map((entry) => {
                const explanation = entryExplanation(entry);
                const details = [
                  entry.email ? `Normalized: ${entry.email}` : undefined,
                  entry.name,
                  explanation,
                ]
                  .filter(Boolean)
                  .join(' — ');
                return (
                  <ListItem key={entry.line} divider>
                    <ListItemText
                      primary={`Line ${entry.line}: ${entry.input}`}
                      secondary={details || undefined}
                    />
                    <Chip
                      label={STATUS_LABELS[entry.status]}
                      color={
                        entry.status === 'ready'
                          ? 'success'
                          : entry.status === 'already_assigned'
                            ? 'info'
                            : 'error'
                      }
                      size="small"
                    />
                  </ListItem>
                );
              })}
            </List>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isApplying}>
          Cancel
        </Button>
        <Button
          onClick={handleReview}
          disabled={!emailsText.trim() || isReviewing || isApplying}
          variant="outlined"
        >
          {isReviewing ? 'Reviewing…' : 'Review'}
        </Button>
        <Button
          onClick={handleApply}
          disabled={!review?.can_apply || !reviewConfirmed || isReviewing || isApplying}
          variant="contained"
        >
          {isApplying ? 'Applying…' : `Apply${review ? ` (${review.summary.ready} new)` : ''}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
